<div class="ai-settings-page">
  <div class="ai-settings-shell">
    <div class="ai-settings-hero">
      <p class="eyebrow">AI-inställningar</p>
      <h1 class="tk-skolar-latin">Hjälp AI Agenten att svara tydligare</h1>
      <p class="hero-copy">
        Överblicka drift, veckogräns, modell och instruktioner på samma sida.
      </p>
    </div>

    <div *ngIf="loading" class="status-card">
      <mat-spinner diameter="20"></mat-spinner>
      <span>Hämtar AI-inställningar...</span>
    </div>

    <div *ngIf="loadError" class="status-card status-card-error">
      <mat-icon>error_outline</mat-icon>
      <span>{{ loadError }}</span>
    </div>

    <!--
      Drift before behaviour, and not in a column: an ops page is read in that
      order - is it running, and if not why - and only then how it should
      behave. The pause lives here rather than beside the model because it is
      an incident control, not a routine setting, and it must be where the eye
      lands first.
    -->
    <!--
      Deliberately not gated on loadError any more. The error above says what
      could not be read; hiding drift, the model and the budget as well turns
      one failed request into a page that looks broken - which is exactly what
      happened when the prompt request started timing out. Each card already
      renders its own absence.
    -->
    <ng-container *ngIf="!loading">
      <section class="settings-summary" aria-label="Sammanfattning">
        <article class="summary-card">
          <span class="summary-icon"><mat-icon>monitor_heart</mat-icon></span>
          <div>
            <span class="summary-label">Drift</span>
            <strong>{{
              runStatus === 'running'
                ? 'Agenten kör'
                : runStatus === 'paused'
                ? 'Pausad'
                : 'Kontrollera status'
            }}</strong>
          </div>
        </article>
        <article class="summary-card">
          <span class="summary-icon"><mat-icon>bar_chart</mat-icon></span>
          <div>
            <span class="summary-label">Veckogräns</span>
            <!--
              Three states, not two. A null limit means "nobody has set one" on
              a backend that supports budgets, and "we cannot know" on one that
              does not - and the card must not claim the first when it is the
              second.
            -->
            <strong>{{
              usageLimitStatus === 'unsupported'
                ? 'Stöds inte av servern'
                : usageLimitStatus === 'error'
                ? 'Kunde inte läsas'
                : usageLimit
                ? (usageLimit.weeklyCostLimitUsd | number: '1.0-2') +
                  ' USD / vecka'
                : 'Ingen gräns satt'
            }}</strong>
          </div>
        </article>
        <article class="summary-card">
          <span class="summary-icon"><mat-icon>auto_awesome</mat-icon></span>
          <div>
            <span class="summary-label">AI-modell</span>
            <strong>{{ currentModelLabel }}</strong>
          </div>
        </article>
      </section>
      <div class="settings-content">
        <!--
        Drift and the model sit on one row: both answer "what is the agent
        doing right now", and neither needs the full width. The prompt takes
        the row below because it is the only thing here that does.
      -->
        <section class="ops-grid">
          <section class="settings-card drift-card">
            <div class="section-heading">
              <div>
                <h2>
                  Drift
                  <button
                    type="button"
                    class="info-trigger"
                    aria-label="Visa information om drift"
                    [attr.aria-expanded]="openInfo === 'drift'"
                    aria-controls="drift-info"
                    (click)="toggleInfo('drift'); $event.stopPropagation()"
                    (keydown.escape)="openInfo = null"
                    ><mat-icon aria-hidden="true">info_outline</mat-icon
                    ><span
                      id="drift-info"
                      class="info-popover"
                      *ngIf="openInfo === 'drift'"
                      >Status och paus styr vad kunderna kan använda just nu.
                      Vid paus ser kunden att AI Agenten är tillfälligt pausad,
                      medan resten av Wrebit fungerar som vanligt.</span
                    ></button
                  >
                </h2>
                <p>Om AI Agenten svarar kunderna just nu.</p>
              </div>
            </div>

            <!--
            Everything this card knows about drift, inside one live region, so
            a change nobody clicked for is still heard.

            The wrapper carries no *ngIf on purpose: a live region has to be in
            the DOM before its contents change, and one inserted together with
            its contents is announced unreliably - which would lose exactly the
            error-to-running transition after a retry.

            aria-atomic is off, against what role="status" implies on its own.
            The four readings below are alternatives, not one statement, so the
            one that appeared is the useful thing to announce; re-reading the
            whole card including "Uppdateras var 30:e sekund" every time is
            not.

            Left unstyled deliberately. The card is a plain block, nothing here
            is spaced by a sibling combinator or a flex gap, and the margins
            collapse through an empty wrapper - so this changes no layout.
            display:contents would have been the tidier-looking answer and the
            wrong one: it has dropped elements with a role out of the
            accessibility tree in older browsers, which is the one risk a live
            region cannot take.
          -->
            <div
              class="drift-state"
              role="status"
              aria-live="polite"
              aria-atomic="false"
            >
              <!--
              Only when we actually know. Without the endpoint the page has no
              reading at all, and "Agenten kör · Leverantören svarar normalt" next
              to "läget är inte tillgängligt" is a claim the page cannot back.
            -->
              <div
                class="status-row"
                *ngIf="pauseSupported"
                [ngSwitch]="runStatus"
              >
                <div
                  *ngSwitchCase="'running'"
                  class="status-chip status-chip-ok"
                >
                  <mat-icon>check_circle</mat-icon>
                  <div>
                    <strong>Agenten kör</strong>
                    <!--
                    Two separate facts, and only one of them is always known.
                    That nobody has paused the agent is read straight from the
                    answer; what the provider is doing we know only if the
                    backend told us, and one without the breaker exposed tells us
                    nothing about it at all.
                  -->
                    <p *ngIf="providerState === 'ok'">
                      Leverantören svarar normalt.
                    </p>
                    <p *ngIf="providerState === 'unknown'">
                      Ingen paus är satt. Leverantörsläget rapporteras inte av
                      den här backenden.
                    </p>
                    <!--
                    Only after a pause has been lifted at least once. "Agenten
                    kör" alone leaves the reader unable to tell a pause that was
                    just lifted from one that never happened, which is the
                    question once an incident is over.
                  -->
                    <p *ngIf="resumedSinceLabel">
                      Återupptagen {{ resumedSinceLabel }}
                    </p>
                  </div>
                </div>

                <div
                  *ngSwitchCase="'paused'"
                  class="status-chip status-chip-paused"
                >
                  <mat-icon>pause_circle</mat-icon>
                  <div>
                    <strong>Pausad av oss</strong>
                    <p>
                      <span *ngIf="pausedSinceLabel"
                        >Sedan {{ pausedSinceLabel }}</span
                      >
                      <span *ngIf="pause.note"> · {{ pause.note }}</span>
                    </p>
                  </div>
                </div>

                <div
                  *ngSwitchCase="'provider-down'"
                  class="status-chip status-chip-down"
                >
                  <mat-icon>cloud_off</mat-icon>
                  <div>
                    <strong>Leverantören svarar inte</strong>
                    <p>
                      <span *ngIf="providerSinceLabel"
                        >Brytaren slog {{ providerSinceLabel }}</span
                      >
                      <span *ngIf="pause.provider?.reason">
                        · {{ pause.provider?.reason }}</span
                      >
                    </p>
                  </div>
                </div>
              </div>

              <!--
            Said, not implied. The breaker lives in memory in wrebit-bookkeep and
            prod runs two replicas, so this is what the instance that answered has
            seen - during a real outage they agree within a minute, but an admin
            reading a single number deserves to know which kind of number it is.
          -->
              <p class="helper-text" *ngIf="pauseSupported && !statusStale">
                <span *ngIf="providerState !== 'unknown'"
                  >Leverantörsläget är senast observerat av en instans, inte ett
                  samlat värde.
                </span>
                Uppdateras var 30:e sekund.
              </p>

              <!--
              Said out loud rather than left to look current. The row still shows
              the last reading we got - there is nothing better to put there - but
              a card whose whole job is to say what is happening right now must
              not go on implying it knows.
            -->
              <p
                class="helper-text helper-text-stale"
                *ngIf="pauseSupported && statusStale"
              >
                <mat-icon>sync_problem</mat-icon>
                Kunde inte uppdatera driftläget.
                <span *ngIf="statusReadLabel"
                  >Visar läget från {{ statusReadLabel }}.</span
                >
                Nytt försök om 30 sekunder.
              </p>

              <div class="soft-info-box" *ngIf="pauseStatus === 'unsupported'">
                <strong
                  >Driftläget är inte tillgängligt i den här miljön</strong
                >
                <p>
                  Backend saknar ännu endpointen. Status och paus dyker upp här
                  när den är utrullad.
                </p>
              </div>

              <!--
              Kept apart from the 404 above on purpose. "Endpointen finns inte"
              and "endpointen svarade inte" send an admin to two different
              problems, and during an incident the wrong one costs the time this
              card exists to save. Only this one gets a retry - nothing pressed
              here makes a missing deploy appear.
            -->
              <div
                class="soft-info-box soft-info-box-error"
                *ngIf="pauseStatus === 'error'"
              >
                <strong>Driftläget kunde inte läsas</strong>
                <p>
                  {{ pauseLoadError }} Status och paus visas inte förrän vi fått
                  svar. Systemprompt och modell nedan påverkas inte.
                </p>
                <button
                  mat-stroked-button
                  class="retry-button"
                  (click)="onRetryPause()"
                  [disabled]="retryingPause"
                >
                  <mat-spinner
                    *ngIf="retryingPause"
                    diameter="18"
                  ></mat-spinner>
                  <span>{{
                    retryingPause ? 'Försöker...' : 'Försök igen'
                  }}</span>
                </button>
              </div>
            </div>

            <mat-form-field
              appearance="outline"
              class="w-full block"
              *ngIf="pauseSupported && !pause.paused"
            >
              <mat-label
                >Varför pausar du? (valfri, syns bara internt)</mat-label
              >
              <input
                matInput
                [(ngModel)]="pauseNote"
                placeholder="Till exempel: agenten föreslår fel momssats på livsmedel"
              />
            </mat-form-field>

            <div class="actions-row" *ngIf="pauseSupported">
              <div class="inline-status">
                <span *ngIf="pauseError" class="feedback error-feedback">
                  <mat-icon>error_outline</mat-icon>
                  {{ pauseError }}
                </span>
              </div>

              <button
                mat-flat-button
                [color]="pause.paused ? 'primary' : 'warn'"
                class="save-button"
                (click)="onTogglePause()"
                [disabled]="savingPause"
              >
                <mat-spinner *ngIf="savingPause" diameter="18"></mat-spinner>
                <span>
                  {{
                    savingPause
                      ? 'Sparar...'
                      : pause.paused
                      ? 'Återuppta AI Agenten'
                      : 'Pausa AI Agenten'
                  }}
                </span>
              </button>
            </div>
          </section>
          <section class="settings-card model-card">
            <div class="section-heading">
              <div>
                <h2>
                  AI-modell
                  <button
                    type="button"
                    class="info-trigger"
                    aria-label="Visa information om AI-modell"
                    [attr.aria-expanded]="openInfo === 'model'"
                    aria-controls="model-info"
                    (click)="toggleInfo('model'); $event.stopPropagation()"
                    (keydown.escape)="openInfo = null"
                    ><mat-icon aria-hidden="true">info_outline</mat-icon
                    ><span
                      id="model-info"
                      class="info-popover"
                      *ngIf="openInfo === 'model'"
                      >Modellen som används när AI Agenten svarar. Ändringar
                      börjar gälla för nya frågor efter att de har
                      sparats.</span
                    ></button
                  >
                </h2>
              </div>

              <span
                class="pill"
                [class.pill-warning]="
                  modelStatus === 'unknown' || hasUnsavedModelChange
                "
                [class.pill-success]="
                  modelStatus !== 'unknown' && !hasUnsavedModelChange
                "
              >
                {{
                  modelStatus === 'unknown'
                    ? 'Kunde inte läsas'
                    : hasUnsavedModelChange
                      ? 'Osparat'
                      : 'Sparat'
                }}
              </span>
            </div>

            <div class="current-model-box">
              <span class="current-model-label">Nuvarande modell</span>
              <strong class="current-model-value">{{
                currentModelLabel
              }}</strong>
              <!--
              Worth showing: a model change that quietly reverted looks
              exactly like an old timestamp on a value someone believes they
              just set. That has happened here.
            -->
              <span class="saved-stamp" *ngIf="modelChangedLabel">
                Ändrad {{ modelChangedLabel }}
              </span>
              <span
                class="saved-stamp saved-stamp-unknown"
                *ngIf="modelStampUnknown"
              >
                Ändrad: tidpunkten kunde inte läsas
              </span>
            </div>

            <mat-form-field appearance="outline" class="w-full block">
              <mat-label>Välj modell</mat-label>
              <mat-select
                [(ngModel)]="selectedModel"
                (ngModelChange)="onSelectedModelChange()"
                [disabled]="modelStatus === 'unknown' || savingModel"
              >
                <mat-option
                  *ngFor="let model of availableModels"
                  [value]="model"
                  >{{ model }}</mat-option
                >
              </mat-select>
            </mat-form-field>

            <div class="save-confirmation success-box" *ngIf="modelSaved">
              <mat-icon>check_circle</mat-icon>
              <span
                >Modellen sparades och kommer att användas av AI Agenten.</span
              >
            </div>

            <div class="actions-row model-actions">
              <div class="inline-status">
                <span *ngIf="modelSaveError" class="feedback error-feedback">
                  <mat-icon>error_outline</mat-icon>
                  {{ modelSaveError }}
                </span>
              </div>

              <button
                mat-flat-button
                color="primary"
                class="save-button"
                (click)="onSaveModel()"
                [disabled]="
                  modelStatus === 'unknown' ||
                  savingModel ||
                  !hasUnsavedModelChange
                "
              >
                <mat-spinner *ngIf="savingModel" diameter="18"></mat-spinner>
                <span>{{ savingModel ? 'Sparar...' : 'Spara modell' }}</span>
              </button>
            </div>
          </section>
          <!--
          In the ops row rather than below the prompt: this and the two cards
          beside it are all levers on what the agent costs and does today, while
          the prompt is how it behaves. A budget is read in the same breath as
          "is it running".
        -->
          <section
            class="settings-card budget-card"
            *ngIf="usageLimitStatus === 'ok'"
          >
            <div class="section-heading">
              <div>
                <h2>
                  Veckogräns
                  <button
                    type="button"
                    class="info-trigger"
                    aria-label="Visa information om veckogräns"
                    [attr.aria-expanded]="openInfo === 'budget'"
                    aria-controls="budget-info"
                    (click)="toggleInfo('budget'); $event.stopPropagation()"
                    (keydown.escape)="openInfo = null"
                    ><mat-icon aria-hidden="true">info_outline</mat-icon
                    ><span
                      id="budget-info"
                      class="info-popover"
                      *ngIf="openInfo === 'budget'"
                      >Bestämmer hur mycket varje företag får använda AI Agenten
                      per vecka. Veckan börjar på måndag och enskilda
                      företagsgränser kan justeras i företagsvyn.</span
                    ></button
                  >
                </h2>
                <p>Hur mycket AI Agent varje företag får använda per vecka.</p>
              </div>

              <span
                class="pill"
                [class.pill-warning]="hasUnsavedBudgetChange"
                [class.pill-success]="!hasUnsavedBudgetChange"
              >
                {{ hasUnsavedBudgetChange ? 'Osparat' : 'Sparat' }}
              </span>
            </div>

            <!--
            Not a zero. An unset budget means nothing is enforced for anyone,
            which is the opposite of what "$0" would say.
          -->
            <p class="helper-text" *ngIf="!usageLimit">
              Ingen gräns är satt. Inget blockeras för någon kund förrän en
              gräns sparas här.
            </p>

            <div class="budget-fields">
              <mat-form-field appearance="outline">
                <!-- Short because the card is half width and the subtitle above
                   already says it is per company. The full label was truncated. -->
                <mat-label>Budget per vecka (USD)</mat-label>
                <input
                  matInput
                  type="number"
                  min="0"
                  step="0.5"
                  [(ngModel)]="budgetUsd"
                  (ngModelChange)="onBudgetChange()"
                />
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Varna vid (% av budgeten)</mat-label>
                <input
                  matInput
                  type="number"
                  min="1"
                  max="99"
                  step="1"
                  [(ngModel)]="warnAtPercent"
                  (ngModelChange)="onBudgetChange()"
                />
              </mat-form-field>
            </div>

            <p class="error-text" *ngIf="budgetError">{{ budgetError }}</p>
            <p class="success-text" *ngIf="budgetSaved">Budgeten är sparad.</p>

            <button
              mat-flat-button
              color="primary"
              class="save-button"
              type="button"
              (click)="saveBudget()"
              [disabled]="savingBudget || !hasUnsavedBudgetChange"
            >
              {{ savingBudget ? 'Sparar …' : 'Spara budget' }}
            </button>
          </section>

          <section
            class="settings-card budget-card budget-card-error"
            *ngIf="usageLimitStatus === 'error'"
          >
            <div class="section-heading">
              <div>
                <h2>Veckogräns</h2>
                <p>Hur mycket AI Agent varje företag får använda per vecka.</p>
              </div>
              <span class="pill pill-warning">Kunde inte läsas</span>
            </div>
            <p class="helper-text">
              Vi kunde inte läsa veckogränsen från servern. Ingen ändring görs
              förrän aktuell gräns har hämtats.
            </p>
          </section>

          <section
            class="settings-card budget-card budget-card-error"
            *ngIf="usageLimitStatus === 'unsupported'"
          >
            <div class="section-heading">
              <div>
                <h2>Veckogräns</h2>
                <p>Hur mycket AI Agent varje företag får använda per vecka.</p>
              </div>
              <span class="pill">Stöds inte</span>
            </div>
            <p class="helper-text">
              Den här servern har ännu inte stöd för veckogränser.
            </p>
          </section>
        </section>

        <section class="settings-card prompt-card">
          <div class="section-heading">
            <div>
              <h2>
                Systemprompt
                <button
                  type="button"
                  class="info-trigger"
                  aria-label="Visa information om systemprompt"
                  [attr.aria-expanded]="openInfo === 'prompt'"
                  aria-controls="prompt-info"
                  (click)="toggleInfo('prompt'); $event.stopPropagation()"
                  (keydown.escape)="openInfo = null"
                  ><mat-icon aria-hidden="true">info_outline</mat-icon
                  ><span
                    id="prompt-info"
                    class="info-popover"
                    *ngIf="openInfo === 'prompt'"
                    >Egna instruktioner läggs ovanpå systemets grundregler.
                    Skriv konkreta regler, undantag eller interna arbetssätt som
                    AI Agenten ska följa.</span
                  ></button
                >
              </h2>
              <p>Skriv egna instruktioner som AI Agenten alltid ska följa.</p>
            </div>

            <span
              class="pill"
              [class.pill-warning]="hasUnsavedChanges"
              [class.pill-success]="!hasUnsavedChanges"
            >
              {{ hasUnsavedChanges ? 'Osparat' : 'Sparat' }}
            </span>
          </div>

          <div
            class="status-card status-card-error"
            *ngIf="promptStatus === 'unknown'"
          >
            <mat-icon>info_outline</mat-icon>
            <span>
              Systemprompten kunde inte läsas. Fältet är låst tills servern har
              svarat, så vi inte råkar skriva över en befintlig prompt med ett
              tomt värde.
            </span>
          </div>

          <div class="save-confirmation success-box" *ngIf="promptSaved">
            <mat-icon>check_circle</mat-icon>
            <span>Systemprompten sparades och används vid nästa AI-fråga.</span>
          </div>

          <!--
          Two columns inside the card: the editor keeps a readable line length
          while the width that is left carries the things you read while
          writing - the variables you may use and what the text is for. Full
          bleed gave a 1300px measure, which is unreadable, and a narrow card on
          a wide page just moved the emptiness.
        -->
          <div class="prompt-grid">
            <mat-form-field
              appearance="outline"
              class="w-full block editor-field"
            >
              <mat-label>Extra instruktioner</mat-label>
              <textarea
                matInput
              [(ngModel)]="systemPrompt"
              (ngModelChange)="onSystemPromptChange()"
              [disabled]="promptStatus !== 'ok' || savingPrompt"
                rows="18"
                placeholder="Till exempel: Om det är oklart vilken typ av köp det gäller ska AI Agenten fråga först innan den föreslår moms eller bokföring."
              ></textarea>
            </mat-form-field>

            <aside class="prompt-aside">
              <div class="variable-box">
                <span class="variable-box-label">Tillgängliga variabler</span>
                <div class="variable-list">
                  <code *ngFor="let variable of promptVariables">{{
                    variable
                  }}</code>
                </div>
              </div>
            </aside>
          </div>

          <div class="actions-row">
            <div class="inline-status">
              <span class="character-count"
                >{{ promptCharacterCount }} tecken</span
              >
              <span class="saved-stamp" *ngIf="promptSavedLabel">
                Sparad {{ promptSavedLabel }}
              </span>
              <!--
              Not the same as no stamp. The save landed; it is the read-back
              that did not, and leaving the row blank here would say the
              backend keeps no timestamps at all.
            -->
              <span
                class="saved-stamp saved-stamp-unknown"
                *ngIf="promptStampUnknown"
              >
                Sparad: tidpunkten kunde inte läsas
              </span>

              <span *ngIf="saveError" class="feedback error-feedback">
                <mat-icon>error_outline</mat-icon>
                {{ saveError }}
              </span>
            </div>

            <button
              mat-flat-button
              color="primary"
              class="save-button"
              (click)="onSaveSystemPrompt()"
              [disabled]="
                promptStatus !== 'ok' || savingPrompt || !hasUnsavedChanges
              "
            >
              <mat-spinner *ngIf="savingPrompt" diameter="18"></mat-spinner>
              <span>{{
                savingPrompt ? 'Sparar...' : 'Spara systemprompt'
              }}</span>
            </button>
          </div>
        </section>
      </div>
    </ng-container>
  </div>
</div>
